import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import type { PortalFaqItem } from "@colab/shared";

export type ReorderFaqItemsInput = {
  category: string;
  orderedIds: string[];
};

type Context = {
  previousFaqAdmin: PortalFaqItem[] | undefined;
};

// PATCH /api/admin/portal-content/faq/order
//
// Optimistic — drag-reorder UX needs immediate feedback. Mirrors the
// bucket-7 use-reorder-tier-rows pattern: snapshot the faqAdmin list,
// reposition rows in the dragged category in place (rows in other
// categories stay where they are), rely on onSettled refetch as the
// authoritative position source.
export function useReorderFaqItems() {
  const queryClient = useQueryClient();
  return useMutation<
    { ok: true },
    ApiClientError,
    ReorderFaqItemsInput,
    Context
  >({
    mutationFn: ({ category, orderedIds }) =>
      apiFetch<{ ok: true }>("/admin/portal-content/faq/order", {
        method: "PATCH",
        body: JSON.stringify({ category, orderedIds }),
      }),
    onMutate: async ({ category, orderedIds }) => {
      const key = queryKeys.portalContent.faqAdmin();
      await queryClient.cancelQueries({ queryKey: key });
      const previousFaqAdmin = queryClient.getQueryData<PortalFaqItem[]>(key);
      queryClient.setQueryData<PortalFaqItem[]>(key, (old) => {
        if (!old) return old;
        const byId = new Map<string, PortalFaqItem>(old.map((r) => [r.id, r]));
        if (orderedIds.some((id) => !byId.has(id))) return old;
        const reordered: PortalFaqItem[] = orderedIds.map((id, idx) => {
          const r = byId.get(id);
          if (!r) throw new Error("unreachable: id checked above");
          return { ...r, position: idx };
        });
        const others = old.filter((r) => r.category !== category);
        return [...reordered, ...others];
      });
      return { previousFaqAdmin };
    },
    onError: (err, _input, context) => {
      if (context?.previousFaqAdmin !== undefined) {
        queryClient.setQueryData(
          queryKeys.portalContent.faqAdmin(),
          context.previousFaqAdmin,
        );
      }
      toast.error(err.message ?? "Failed to reorder FAQ items");
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.portalContent.faqAdmin(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.portalContent.faqPublic(),
      });
    },
  });
}
